import React from 'react';
import { ALL_PLACES, INDEXED_PLACES, pathForPlace } from './seoPlaces';

/**
 * The written half of a /flats-in-<place> page, shown under the listings.
 *
 * <p>Without it a place page was a heading over a grid of cards: nothing a
 * search engine could read as being about Noida in particular, and no links
 * out to related pages. Every figure here is computed from the listings the
 * page has just loaded — the same results the visitor sees above — so the copy
 * stays true as inventory changes and differs from city to city in substance,
 * not just in the place name. Nothing is invented: when there are no listings
 * the stats paragraph and the rent answer are simply left out.
 *
 * <p>Its FAQ carries its own FAQPage markup. The site-wide FAQPage block in
 * public/index.html describes the home page's questions, so scripts/prerender.js
 * strips it from every other route; a page may only mark up questions it
 * actually shows.
 */

const BLUE = '#2563EB';
const INK = '#0F172A';
const MUTED = '#475569';
const LINE = '#E2E8F0';

const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

const median = (xs) => {
    if (!xs.length) return null;
    const s = [...xs].sort((a, b) => a - b);
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/** Keep in step with ALIASES in scripts/generate-seo-places.js. */
const ALIASES = { Bangalore: 'Bengaluru', Gurgaon: 'Gurugram' };

const TYPE_LABEL = { FLAT: 'flats', ROOM: 'private rooms', PG: 'PGs' };
const TYPE_ONE = { FLAT: 'flat', ROOM: 'private room', PG: 'PG' };
const countOf = (t, n) => `${n} ${n === 1 ? TYPE_ONE[t] : TYPE_LABEL[t]}`;

/** "a, b and c" */
const joinAnd = (parts) => (parts.length < 2 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`);

const statsFor = (listings) => {
    const rents = listings.map((l) => l.rent).filter((r) => typeof r === 'number' && r > 0);
    const byType = listings.reduce((a, l) => { if (l.postType) a[l.postType] = (a[l.postType] || 0) + 1; return a; }, {});
    const rentsOf = (t) => listings.filter((l) => l.postType === t && l.rent > 0).map((l) => l.rent);

    const areaCount = new Map();
    listings.forEach((l) => {
        const n = (l.location?.name || '').trim();
        if (n) areaCount.set(n, (areaCount.get(n) || 0) + 1);
    });
    const areas = [...areaCount.entries()].sort((a, b) => b[1] - a[1]).map(([n]) => n);

    return {
        total: listings.length,
        min: rents.length ? Math.min(...rents) : null,
        max: rents.length ? Math.max(...rents) : null,
        median: median(rents),
        byType,
        medianOf: { FLAT: median(rentsOf('FLAT')), ROOM: median(rentsOf('ROOM')), PG: median(rentsOf('PG')) },
        areas,
    };
};

const buildFaq = (place, s) => {
    const where = place.name;
    const faq = [];

    if (s.total && s.median) {
        const parts = ['FLAT', 'ROOM', 'PG']
            .filter((t) => s.medianOf[t])
            .map((t) => `${TYPE_LABEL[t]} around ${inr(s.medianOf[t])}`);
        faq.push({
            q: `How much is rent for a flat or room in ${where}?`,
            a: `Across the ${s.total} newest listings on FlatLoop near ${where}, asking rents run from ${inr(s.min)} to ${inr(s.max)} a month, with a typical (median) rent of ${inr(s.median)}${parts.length ? ` — ${joinAnd(parts)}` : ''}. Rent Insights shows what tenants in each area actually report paying.`,
        });
    }

    faq.push({
        q: `How do I find a PG or a shared room in ${where}?`,
        a: `Search ${where} on FlatLoop and filter by type (flat, private room or PG), budget, gender preference, furnishing and occupancy. If nothing fits, post a requirement and owners in ${where} can contact you directly.`,
    });
    faq.push({
        q: `How do I find a flatmate in ${where}?`,
        a: `Many ${where} listings are a room in a shared flat posted by the people already living there. Each listing shows who the flat is looking for, and FlatLoop's lifestyle match compares their habits — food, smoking, pets, work hours — with yours before you get in touch.`,
    });
    faq.push({
        q: `Is there any brokerage on flats in ${where}?`,
        a: 'No. Owners and tenants deal with each other directly, so there is no broker, no brokerage and no commission on any deal. Searching and posting are free; FlatLoop Prime (₹99 for 30 days) is only needed to see an owner’s number and start a chat.',
    });
    return faq;
};

const linkStyle = { color: BLUE, fontWeight: 600, textDecoration: 'none' };

const PlaceSeoSection = ({ place, listings, total, loading }) => {
    if (!place) return null;
    const city = place.kind === 'city' ? place.name : place.city;
    const s = statsFor(listings || []);
    const faq = buildFaq(place, s);

    // Area pages this city has, plus the areas present in the results. Only
    // area pages that exist are linked; the rest are named as plain text.
    const placeByName = ALL_PLACES.reduce((a, p) => { a[p.name.toLowerCase()] = p; return a; }, {});
    const cityAreas = INDEXED_PLACES.filter((p) => p.kind === 'locality' && p.city === city && p.slug !== place.slug);
    const resultAreas = s.areas
        .filter((n) => n.toLowerCase() !== place.name.toLowerCase())
        .slice(0, 12);
    const cityPlace = place.kind === 'locality' ? ALL_PLACES.find((p) => p.kind === 'city' && p.name === city) : null;
    const otherCities = ALL_PLACES.filter((p) => p.kind === 'city' && p.slug !== place.slug && p !== cityPlace);

    const faqLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    };

    const typeParts = ['FLAT', 'ROOM', 'PG'].filter((t) => s.byType[t]).map((t) => countOf(t, s.byType[t]));
    // The API returns one page; the total is the honest headline number, and
    // the breakdown and rents describe the listings actually shown above.
    const count = Math.max(total || 0, s.total);
    const partial = count > s.total;

    return (
        <section aria-labelledby="place-about" style={{ maxWidth: '1100px', margin: '56px auto 0', padding: '0 4px', color: INK }}>
            <h2 id="place-about" style={{ fontSize: 'clamp(22px, 3vw, 30px)', fontWeight: 800, letterSpacing: '-.5px', margin: 0 }}>
                Renting in {place.name}{place.kind === 'locality' ? `, ${city}` : ''}{place.kind === 'city' && ALIASES[place.name] ? ` (${ALIASES[place.name]})` : ''}
            </h2>

            <p style={{ fontSize: '16px', lineHeight: 1.7, color: MUTED, marginTop: '12px', maxWidth: '820px' }}>
                {!loading && s.total > 0 ? (
                    <>
                        There {count === 1 ? 'is 1 place' : `are ${count} flats, rooms and PGs`} for rent within 20 km of {place.name} on FlatLoop right now.
                        {typeParts.length > 0 && <> {partial ? `The ${s.total} newest are` : 'They are'} {joinAnd(typeParts)}</>}
                        {s.min && <>{typeParts.length > 0 ? ', with' : ' Their'} asking rents from {inr(s.min)} to {inr(s.max)} a month and a typical rent of {inr(s.median)}.</>}
                        {typeParts.length > 0 && !s.min && '.'}
                        {' '}Every one is posted by a phone-verified owner or tenant, and you deal with them directly — no broker and no brokerage.
                    </>
                ) : (
                    <>
                        FlatLoop lists flats, private rooms and PG accommodation for rent in {place.name}, posted by
                        phone-verified owners and tenants. You deal with them directly — no broker and no brokerage.
                    </>
                )}
            </p>

            {(cityAreas.length > 0 || resultAreas.length > 0) && (
                <>
                    <h3 style={{ fontSize: '17px', fontWeight: 800, margin: '28px 0 10px' }}>Popular areas near {place.name}</h3>
                    <p style={{ fontSize: '15px', lineHeight: 2, color: MUTED, margin: 0 }}>
                        {cityAreas.map((p) => (
                            <React.Fragment key={p.slug}>
                                <a href={pathForPlace(p)} style={linkStyle}>Flats & PG in {p.name}</a>{' · '}
                            </React.Fragment>
                        ))}
                        {resultAreas
                            .filter((n) => !cityAreas.some((p) => p.name.toLowerCase() === n.toLowerCase()))
                            .map((n, i, arr) => {
                                const p = placeByName[n.toLowerCase()];
                                return (
                                    <React.Fragment key={n}>
                                        {p && p.index ? <a href={pathForPlace(p)} style={linkStyle}>{n}</a> : <span>{n}</span>}
                                        {i < arr.length - 1 ? ' · ' : ''}
                                    </React.Fragment>
                                );
                            })}
                    </p>
                </>
            )}

            <h3 style={{ fontSize: '17px', fontWeight: 800, margin: '28px 0 12px' }}>Questions about renting in {place.name}</h3>
            <div style={{ display: 'grid', gap: '10px' }}>
                {faq.map((f) => (
                    <details key={f.q} style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: '14px', padding: '14px 18px' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: '15.5px' }}>{f.q}</summary>
                        <p style={{ margin: '10px 0 0', fontSize: '15px', lineHeight: 1.65, color: MUTED }}>{f.a}</p>
                    </details>
                ))}
            </div>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

            <h3 style={{ fontSize: '17px', fontWeight: 800, margin: '28px 0 10px' }}>
                {cityPlace ? `More in ${city} and other cities` : 'Flats, rooms and PG in other cities'}
            </h3>
            <p style={{ fontSize: '15px', lineHeight: 2, color: MUTED, margin: 0 }}>
                {cityPlace && (
                    <><a href={pathForPlace(cityPlace)} style={linkStyle}>Flats, rooms & PG in {city}</a>{' · '}</>
                )}
                {otherCities.map((p, i) => (
                    <React.Fragment key={p.slug}>
                        <a href={pathForPlace(p)} rel={p.index ? undefined : 'nofollow'} style={linkStyle}>Flats & PG in {p.name}</a>
                        {i < otherCities.length - 1 ? ' · ' : ''}
                    </React.Fragment>
                ))}
            </p>
        </section>
    );
};

export default PlaceSeoSection;
